import { useState, useEffect } from 'react';
import { Select, MenuItem, Box, Typography, Avatar } from '@mui/material';
import { getStoredUserData, setSelectedGameId, getSelectedGameId } from '@core/utils/auth';
import type { GameInfo } from '@modules/auth/services/api';
import s1914Image from '@assets/images/s1914.png';
import ww2Image from '@assets/images/ww2.png';
import { Add } from '@mui/icons-material';

const gameImages: Record<string, string> = {
  s1914: s1914Image,
  ww2: ww2Image,
};

export const GameSelect = () => {
  const [selectedGame, setSelectedGame] = useState<number | null>(null);
  const [games, setGames] = useState<GameInfo[]>([]);

  useEffect(() => {
    const userData = getStoredUserData();
    if (userData?.games) {
      setGames(userData.games);
      const currentGameId = getSelectedGameId() || userData.games[0]?.game_id;
      if (currentGameId) {
        setSelectedGame(currentGameId);
        setSelectedGameId(currentGameId);
      }
    }
  }, []);

  const handleGameChange = (gameId: number) => {
    setSelectedGame(gameId);
    setSelectedGameId(gameId);
    // Reload the page to refresh all data with the new game
    window.location.reload();
  };

  if (!games.length) return null;

  const currentGame = games.find((g) => g.game_id === selectedGame);

  return (
    <Select
      value={selectedGame || ''}
      onChange={(e) => handleGameChange(e.target.value as number)}
      sx={{
        minWidth: 200,
        height: 50,
        backgroundColor: 'background.black',
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: 'divider',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: 'primary.main',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: 'primary.main',
        },
      }}
      renderValue={() => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {currentGame && (
            <>
              <Avatar
                src={gameImages[currentGame.game_key] || ''}
                alt={currentGame.game_name}
                sx={{
                  width: 60,
                  height: 35,
                  backgroundColor: 'black',
                  padding: '2px',
                  borderRadius: '5px',
                }}
                imgProps={{ style: { objectFit: 'contain' } }}
              />
              <Typography variant="body2" fontWeight={600}>
                {currentGame.game_name}
              </Typography>
            </>
          )}
        </Box>
      )}
    >
      {games.map((game) => (
        <MenuItem key={game.game_id} value={game.game_id} sx={{ borderRadius: '5px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
            <Avatar
              src={gameImages[game.game_key] || ''}
              alt={game.game_name}
              sx={{ width: 60, height: 35, backgroundColor: 'black', padding: '5px', borderRadius: '5px' }}
              imgProps={{ style: { objectFit: 'contain' } }}
            />
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {game.game_name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {game.internal_name}
              </Typography>
            </Box>
          </Box>
        </MenuItem>
      ))}
      <MenuItem
        sx={{
          borderRadius: '5px',
          textAlign: 'center',
          borderTop: '1px solid',
          borderColor: 'divider',
          cursor: 'not-allowed',
        }}
        disabled={true}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
          <Add />
          <Typography variant="body2" fontWeight={600}>
            Add New Game
          </Typography>
        </Box>
      </MenuItem>
    </Select>
  );
};
export default GameSelect;
